// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { MAX_FILE_BYTES, MAX_FILES, isSupportedImage, remainingAttachmentSlots } from './image-resize';
import { clearFeedbackFiles, loadFeedbackFiles, saveFeedbackFiles } from './feedback-draft-store';

export type FeedbackType = 'bug' | 'suggestion' | 'other';

export interface PickedFile {
  file: File;
  previewUrl: string;
}

/** Prefix of the localStorage keys holding the text half of the draft.
 *  Drafts are scoped per user id: one account must never see (or send)
 *  another account's unsent draft on a shared browser. */
export const DRAFT_STORAGE_PREFIX = 'flaircv:feedback-draft';
/** Pre-scoping key (the unscoped value shipped before the round-2 review),
 *  deleted on sight so an old global draft cannot leak across accounts. */
export const LEGACY_DRAFT_STORAGE_KEY = 'flaircv:\u2026raft';

export function draftStorageKey(scope: string): string {
  return `${DRAFT_STORAGE_PREFIX}:${scope}`;
}

interface PersistedTextDraft {
  type: FeedbackType;
  message: string;
  email: string;
  attachScreenshot: boolean;
}

function readStoredDraft(scope: string): PersistedTextDraft | null {
  if (!scope) return null;
  try {
    localStorage.removeItem(LEGACY_DRAFT_STORAGE_KEY);
    const raw = localStorage.getItem(draftStorageKey(scope));
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || typeof d.message !== 'string' || typeof d.email !== 'string') return null;
    return {
      type: d.type === 'bug' || d.type === 'other' ? d.type : 'suggestion',
      message: d.message,
      email: d.email,
      attachScreenshot: d.attachScreenshot === true,
    };
  } catch {
    return null;
  }
}

/**
 * All form state of the feedback dialog, owned by the (always-mounted)
 * FeedbackBubble so it survives the dialog closing — dismissing the dialog
 * with an outside click, Escape or the ✕ must never throw away what the user
 * typed. Text fields persist in localStorage; picked attachments in
 * IndexedDB; the draft is cleared only on a successful send or an explicit
 * Cancel.
 */
export function useFeedbackDraft(defaultEmail: string, scope: string) {
  // Read the persisted draft exactly once, synchronously, before first paint.
  const initialRef = useRef<PersistedTextDraft | null | undefined>(undefined);
  if (initialRef.current === undefined) initialRef.current = readStoredDraft(scope);
  const initial = initialRef.current;

  const [type, setType] = useState<FeedbackType>(initial?.type ?? 'suggestion');
  const [message, setMessage] = useState(initial?.message ?? '');
  const [email, setEmail] = useState(initial?.email || defaultEmail);
  const [attachScreenshot, setAttachScreenshot] = useState(initial?.attachScreenshot ?? false);
  const [files, setFiles] = useState<PickedFile[]>([]);

  // Object URLs of the thumbnails, tracked in a ref so every created URL is
  // revoked on unmount (a cleanup closure over `files` would only ever see
  // the empty array captured at mount).
  const previewUrlsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  // Mirror of files.length for stable closures; bumped on every mutation and
  // re-synced after commit, so back-to-back calls (pasting five images at
  // once) cannot race past the attachment budget.
  const fileCountRef = useRef(0);
  useEffect(() => {
    fileCountRef.current = files.length;
  }, [files.length]);

  // The opt-in screenshot shares the attachment budget; read through a ref
  // because the paste listener keeps a stable closure over addFiles.
  const screenshotSlotRef = useRef(0);
  useEffect(() => {
    screenshotSlotRef.current = attachScreenshot ? 1 : 0;
  }, [attachScreenshot]);

  // Bumped on every clear so writes still in flight become stale and cannot
  // resurrect a cancelled draft.
  const versionRef = useRef(0);

  // Persist the text half on every change. localStorage is synchronous and
  // small, so a keystroke-level write is fine here. Only a draft that
  // deviates from the defaults is written; a form back at the defaults
  // removes the key, so clearing a draft really clears it. The email is
  // stored only when it differs from the account default, so an old
  // prefilled value never shadows a changed account email.
  useEffect(() => {
    if (!scope) return;
    try {
      const isDefaultDraft =
        message === '' && type === 'suggestion' && !attachScreenshot && email === defaultEmail;
      if (isDefaultDraft) {
        localStorage.removeItem(draftStorageKey(scope));
        return;
      }
      const draft: PersistedTextDraft = {
        type,
        message,
        email: email === defaultEmail ? '' : email,
        attachScreenshot,
      };
      localStorage.setItem(draftStorageKey(scope), JSON.stringify(draft));
    } catch {
      // Storage blocked or full: the draft simply will not survive a reload.
    }
  }, [type, message, email, attachScreenshot, defaultEmail, scope]);

  // Persist attachments (async, IndexedDB).
  useEffect(() => {
    const version = versionRef.current;
    void saveFeedbackFiles(
      scope,
      files.map((f) => f.file),
      () => versionRef.current === version,
    );
  }, [files, scope]);

  // Restore attachments after mount. Dropped if the user already picked files
  // while the database was opening, or cancelled (draft version bumped)
  // while the load was in flight — otherwise the cancelled draft would be
  // resurrected and immediately re-persisted.
  useEffect(() => {
    if (!scope) return;
    let cancelled = false;
    const version = versionRef.current;
    void loadFeedbackFiles(scope).then((storedFiles) => {
      if (cancelled || versionRef.current !== version) return;
      if (fileCountRef.current > 0 || storedFiles.length === 0) return;
      const restored = storedFiles.slice(0, MAX_FILES).map((file) => {
        const previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.add(previewUrl);
        return { file, previewUrl };
      });
      fileCountRef.current = restored.length;
      setFiles(restored);
    });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const addFiles = (incoming: File[]) => {
    const accepted: File[] = [];
    for (const file of incoming) {
      if (!isSupportedImage(file)) {
        toast.error(`"${file.name}" is not a PNG, JPEG, or WebP image.`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`"${file.name}" is larger than 2 MB.`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;

    const room = remainingAttachmentSlots(fileCountRef.current, screenshotSlotRef.current === 1);
    if (accepted.length > room) {
      toast.error(`At most ${MAX_FILES} images can be attached.`);
    }

    const next = accepted.slice(0, Math.max(0, room)).map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.add(previewUrl);
      return { file, previewUrl };
    });
    if (next.length === 0) return;

    fileCountRef.current += next.length;
    setFiles((prev) => [...prev, ...next]);
  };

  const removeFile = (index: number) => {
    const target = files[index];
    if (!target) return;
    URL.revokeObjectURL(target.previewUrl);
    previewUrlsRef.current.delete(target.previewUrl);
    fileCountRef.current = Math.max(0, fileCountRef.current - 1);
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /** Reset everything and wipe the persisted draft. Called on a successful
   *  send and by the dialog's Cancel button — and by nothing else. */
  const clearDraft = () => {
    versionRef.current += 1;
    try {
      localStorage.removeItem(draftStorageKey(scope));
      localStorage.removeItem(LEGACY_DRAFT_STORAGE_KEY);
    } catch {
      // ignore
    }
    void clearFeedbackFiles(scope);
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
    fileCountRef.current = 0;
    setFiles([]);
    setType('suggestion');
    setMessage('');
    setAttachScreenshot(false);
    setEmail(defaultEmail);
  };

  const hasDraft = message.trim().length > 0 || files.length > 0;

  return {
    type,
    setType,
    message,
    setMessage,
    email,
    setEmail,
    attachScreenshot,
    setAttachScreenshot,
    files,
    addFiles,
    removeFile,
    clearDraft,
    hasDraft,
  };
}

export type FeedbackDraft = ReturnType<typeof useFeedbackDraft>;
